/**
 * Thin, SSR-safe wrappers around the Refiner (https://refiner.io) JavaScript
 * client. Refiner replaces Appzi for in-product CSAT / feedback capture.
 *
 * Targeting, per-audience copy, trigger rules (immediate / time-delay /
 * page-visit / tracked-event) and Slack routing are all configured in the
 * Refiner dashboard. The frontend's job is only to load the SDK, identify the
 * logged-in user with the traits the dashboard segments on, and (optionally)
 * fire events / launch forms for interaction-based triggers.
 *
 * The `refiner-js` package touches `window` on import, so it is loaded lazily
 * via dynamic import and guarded against server-side execution.
 *
 * Every command goes through a single serial queue, and none of them runs until
 * `initRefiner` has called `setProject`. Callers are React effects in different
 * components (the loader inits, `OnClientUserLoad` identifies) that fire in tree
 * order, not in the order the SDK needs. Commands issued early simply wait, and
 * if `initRefiner` is never called (flag off, no project ID, kill switch) they
 * never run, so the SDK is never even imported.
 */

import type { RefinerUserTraits } from './traits'

type RefinerFn = (method: string, ...args: unknown[]) => void

/**
 * Where the SDK persists its identifiers/timestamps. `localStorage` is used
 * here rather than the SDK default (`localStorageFallbackCookies`) so Refiner
 * can never fall back to setting cookies.
 */
export type RefinerStorageMethod = 'localStorageFallbackCookies' | 'localStorage' | 'sessionStorage'

/**
 * Everything the client needs to run Refiner for this request. Built on the
 * server by each app's `lib/refiner.server.ts`, which is the single place that
 * decides whether Refiner is active, and handed to both consumers (the loader
 * and the identify call) so the two can't disagree.
 */
export interface RefinerConfig {
  projectId: string
  /** Refiner contact id: the Knock person id. The signature is computed from this. */
  personId: string
  /** Sent as a trait so Refiner → Customer.io sync can key on it. */
  email?: string
  /** Identity Verification HMAC for the signed-in user. */
  signature?: string
  /** Survey (Manual trigger) opened by the edge feedback tab. */
  feedbackFormId?: string
}

/** The result of that decision, as passed down from a layout. */
export interface RefinerSetup {
  /**
   * Refiner is the feedback vendor for this request, so Appzi is not loaded and
   * the Appzi identity is not set. This is the feature flag on its own: it stays
   * true even when Refiner itself can't run, so we never fall back to Appzi while
   * the flag is on.
   */
  enabled: boolean
  /** Present only when Refiner can actually run. Undefined means load nothing. */
  config?: RefinerConfig
}

export interface InitRefinerOptions {
  /** Defaults to 'localStorage' to disable the SDK's cookie fallback. */
  storageMethod?: RefinerStorageMethod
}

/**
 * Where the last identified person id is remembered, so a different person on
 * the same browser can be told apart. Refiner's own identity is in localStorage
 * too, so the two are cleared together.
 */
const LAST_IDENTIFIED_ID_KEY = 'refiner:last-identified-id'

let refinerPromise: Promise<RefinerFn | null> | null = null
let initPromise: Promise<void> | null = null
let markReady: () => void = () => {}
const ready = new Promise<void>((resolve) => {
  markReady = resolve
})
let queue: Promise<void> = Promise.resolve()

/**
 * Whether an identify has been queued yet, as a tiny external store so the
 * feedback tab can wait for it (see `useRefinerIdentified`). The flag is set as
 * soon as identify is *queued*, not when it runs: every command goes through one
 * serial queue, so from that moment a `showForm` can only run after the user has
 * been identified. Without this the tab would render from the server session
 * while identify was still waiting on the client user, and a click in that gap
 * would open a survey Refiner could not attribute.
 */
let hasIdentified = false
const identifiedListeners = new Set<() => void>()

export function subscribeRefinerIdentified(listener: () => void): () => void {
  identifiedListeners.add(listener)
  return () => {
    identifiedListeners.delete(listener)
  }
}

export function getRefinerIdentified(): boolean {
  return hasIdentified
}

function markIdentified() {
  if (hasIdentified) return
  hasIdentified = true
  identifiedListeners.forEach((listener) => listener())
}

function readLastIdentifiedId(): string | null {
  try {
    return window.localStorage.getItem(LAST_IDENTIFIED_ID_KEY)
  } catch {
    // Storage can be blocked (e.g. private mode); treat it as nothing remembered.
    return null
  }
}

function writeLastIdentifiedId(id: string) {
  try {
    window.localStorage.setItem(LAST_IDENTIFIED_ID_KEY, id)
  } catch {
    // See readLastIdentifiedId.
  }
}

/**
 * Load `refiner-js` once, lazily.
 *
 * Only a failure to load the npm chunk is retried below. If the chunk loads but
 * the SDK's own script (`js.refiner.io/v001/client.js`) is blocked — an ad
 * blocker, say — `refiner-js` just logs a warning, so init here looks
 * successful and every later command silently does nothing. That is deliberate:
 * feedback is not worth fighting a blocker over, and nothing else breaks.
 */
function getRefiner(): Promise<RefinerFn | null> {
  if (typeof window === 'undefined') return Promise.resolve(null)

  if (!refinerPromise) {
    refinerPromise = import('refiner-js')
      .then((mod) => ((mod as { default?: RefinerFn }).default ?? (mod as unknown as RefinerFn)) as RefinerFn)
      .catch((err) => {
        console.error('[refiner] failed to load client', err)
        // Don't cache the failure: clearing the promise lets the next command
        // retry the import instead of skipping every command for the page's life.
        refinerPromise = null
        return null
      })
  }

  return refinerPromise
}

/** Run a command after `initRefiner` and every previously queued command. */
function enqueue(command: (refiner: RefinerFn) => void): Promise<void> {
  queue = queue
    .then(async () => {
      await ready
      const refiner = await getRefiner()
      if (refiner) command(refiner)
    })
    .catch((err) => {
      console.error('[refiner] command failed', err)
    })

  return queue
}

/**
 * Initialize the Refiner client and release every command waiting on it. Safe to
 * call more than once (e.g. React strict mode); only the first successful call
 * does anything. If the SDK fails to load, a later call retries.
 *
 * This is the first SDK touch, and therefore the first request to js.refiner.io.
 * Refiner needs no cookie consent here: storage is forced to `localStorage`, so
 * it never sets cookies.
 */
export function initRefiner(projectId?: string, options: InitRefinerOptions = {}): Promise<void> {
  if (!projectId) return Promise.resolve()
  if (initPromise) return initPromise

  const { storageMethod = 'localStorage' } = options

  initPromise = getRefiner()
    .then((refiner) => {
      if (!refiner) {
        initPromise = null
        return
      }
      refiner('setProject', projectId)
      refiner('setClientStorageMethod', storageMethod)
      markReady()
    })
    .catch((err) => {
      console.error('[refiner] init failed', err)
      initPromise = null
    })

  return initPromise
}

/**
 * Identify the current user and attach traits for audience targeting.
 *
 * `signature` is the Identity Verification HMAC. It must be computed on the
 * server (each app's `src/lib/refiner.server.ts`) and is required once Identity
 * Verification is enabled in the Refiner dashboard.
 *
 * Refiner keeps the identified user in localStorage, so a browser that last
 * served another person would otherwise carry that person into this one.
 * Logout is a full-page navigation and sessions also expire or end in another
 * tab, so there is no reliable place to clear it then. Instead, when the ID
 * differs from the last one identified here, the previous user is reset first.
 * Refiner keeps an anonymous identifier in localStorage after a reset, so this
 * drops the person link but is not a full wipe.
 */
export function identifyRefinerUser(traits: RefinerUserTraits, signature?: string): Promise<void> {
  if (!traits?.id) return Promise.resolve()

  markIdentified()

  return enqueue((refiner) => {
    const previousId = readLastIdentifiedId()
    if (previousId && previousId !== traits.id) refiner('resetUser')

    if (signature) {
      refiner('identifyUser', traits, { signature })
    } else {
      refiner('identifyUser', traits)
    }
    writeLastIdentifiedId(traits.id)
  })
}

/**
 * Fire a tracked event, e.g. to satisfy an interaction-based trigger.
 * Refiner only records that the event occurred — attributes cannot be attached.
 * Anything that needs to be segmented on belongs in `identifyRefinerUser`.
 */
export function trackRefinerEvent(name: string): Promise<void> {
  if (!name) return Promise.resolve()
  return enqueue((refiner) => refiner('trackEvent', name))
}

/** Manually launch a survey/form (e.g. from a custom feedback button). */
export function showRefinerForm(formId: string): Promise<void> {
  if (!formId) return Promise.resolve()
  return enqueue((refiner) => refiner('showForm', formId))
}
